import { forwardRef, useImperativeHandle, useState } from "react";

import { Modal } from "../../components/modal";

import type { FormMode, ForwardFormRef } from "./shared";
import type { ForwardedRef, JSX, RefObject } from "react";

type AddDomainFormProps =
  | { mode: "INSERT" }
  | { mode: "EDIT"; selectedDomain: string | undefined };

const AddDomainForm = (
  props: AddDomainFormProps,
  ref: ForwardedRef<{
    validate: () => boolean;
    getResult: () => string;
    getMode: () => FormMode;
  }>,
): JSX.Element => {
  const [domain, setDomain] = useState<string | undefined>(
    props.mode === "EDIT" ? props.selectedDomain : undefined,
  );
  const [domainError, setDomainError] = useState<string | undefined>();

  const isValidDomain = (domain: string): boolean => {
    const domainRegex =
      /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
    return domainRegex.test(domain);
  };

  useImperativeHandle(ref, () => ({
    getResult: (): string => domain || "",
    getMode: (): FormMode => props.mode,
    validate: (): boolean => {
      if (!domain) {
        setDomainError("Angabe notwendig");
        return false;
      }

      if (!isValidDomain(domain)) {
        setDomainError("Ungütlige Domain");
        return false;
      }

      return true;
    },
  }));

  return (
    <>
      <div className="mb-3">
        <label htmlFor="domain" className="form-text">
          Domain
        </label>

        <input
          type="text"
          id="domain"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className={domainError ? "form-control is-invalid" : "form-control"}
          autoFocus
        />
        <div id="domainFeedback" className="invalid-feedback">
          {domainError}
        </div>
      </div>
    </>
  );
};

const AddDomainModalContent = forwardRef(AddDomainForm);

export type DomainModalProps = {
  isAddDomainModalOpen: boolean;
  setIsAddDomainModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDomain: string | undefined;
  domainFormRef: RefObject<ForwardFormRef<string> | null>;
  handleAddDomainSubmit: () => void;
};

export const DomainModal: React.FC<DomainModalProps> = ({
  isAddDomainModalOpen,
  setIsAddDomainModalOpen,
  selectedDomain,
  domainFormRef,
  handleAddDomainSubmit,
}: DomainModalProps) => {
  return (
    <Modal
      isOpen={isAddDomainModalOpen}
      onClose={() => setIsAddDomainModalOpen(false)}
      title="Domain Hinzufügen"
      primaryAction={{
        label: "OK",
        onClick: (e) => {
          e.preventDefault();
          handleAddDomainSubmit();
        },
      }}
      secondaryAction={{
        label: "Abbrechen",
        onClick: (e) => {
          e.preventDefault();
          setIsAddDomainModalOpen(false);
        },
      }}
    >
      <AddDomainModalContent
        ref={domainFormRef}
        {...(selectedDomain
          ? { mode: "EDIT", selectedDomain }
          : { mode: "INSERT" })}
      />
    </Modal>
  );
};
